import React, { useState, useEffect } from "react";
import { Dropdown, Button, Icon } from "../../components/tailwind";
import { Option } from "../../components/tailwind/dropdown/Dropdown";
import { Spinner } from "../../utils/Spinner";
import {
  validateFieldset,
  ValidatedFieldsetField,
} from "../../api/fieldsetApi";
import { useCreateCustomerFieldMutation } from "../../api/customerFieldApi";
import { CustomerFieldEditModal } from "../../customers/customer-fields/CustomerFieldEditModal";
import { showMessage } from "../../utils/partialUtils";
import { NotificationAppearance } from "../../api/djangoToastSlice";

interface EnrichFieldsetSelectorProps {
  fieldsetOptions: Option<number>[];
  selectedFieldset: number | null;
  onFieldsetChange: (fieldsetId: number) => void;
  onEnrich: () => void;
  isEnriching: boolean;
  disabled?: boolean;
  isFileInvalid?: boolean;
  token: string;
  productId?: string;
  result_exists: boolean;
  onViewResult: () => void;
}

export const EnrichFieldsetSelector: React.FC<EnrichFieldsetSelectorProps> = ({
  fieldsetOptions,
  selectedFieldset,
  onFieldsetChange,
  onEnrich,
  isEnriching,
  disabled = false,
  isFileInvalid = false,
  token,
  productId,
  result_exists,
  onViewResult,
}) => {
  const [validationResults, setValidationResults] = useState<
    ValidatedFieldsetField[]
  >([]);
  const [isValidating, setIsValidating] = useState(false);
  const [validationError, setValidationError] = useState<string | null>(null);
  const [editingFieldId, setEditingFieldId] = useState<number | null>(null);

  const [
    createCustomerField,
    { isLoading: isCreating },
  ] = useCreateCustomerFieldMutation();

  const allFieldsValid = validationResults.every((f) => f.is_valid_field);

  const sortedValidationResults = [...validationResults].sort((a, b) => {
    const rank = (f: ValidatedFieldsetField): number => {
      if (!f.is_valid_field) return 0;
      if (f.existing_pim_value) return 1;
      return 2;
    };
    return rank(a) - rank(b);
  });

  const fetchValidation = async (): Promise<void> => {
    if (!selectedFieldset || !token) {
      setValidationResults([]);
      return;
    }

    setIsValidating(true);
    setValidationError(null);

    try {
      const results = await validateFieldset(
        token,
        selectedFieldset,
        productId
      );
      setValidationResults(results);
    } catch (error) {
      console.error("Failed to validate fieldset:", error);
      setValidationError("Failed to validate fieldset");
      setValidationResults([]);
    } finally {
      setIsValidating(false);
    }
  };

  useEffect(() => {
    fetchValidation();
  }, [selectedFieldset, token, productId]);

  const handleEditField = (field: ValidatedFieldsetField): void => {
    if (field.customer_field_id) {
      setEditingFieldId(field.customer_field_id);
    }
  };

  const handleCreateField = async (fieldKey: string): Promise<void> => {
    try {
      await createCustomerField({
        field_id: fieldKey,
        name: fieldKey,
      }).unwrap();

      showMessage(
        NotificationAppearance.SUCCESS,
        `Created PIM Field: ${fieldKey}`
      );
      await fetchValidation();
    } catch (error) {
      showMessage(NotificationAppearance.ERROR, "Failed to create field");
      console.error("Failed to create field:", error);
    }
  };

  const handleModalSave = async (): Promise<void> => {
    setEditingFieldId(null);
    await fetchValidation();
  };

  return (
    <div className="tw-mb-6 tw-bg-white tw-shadow tw-rounded-lg">
      <div className="tw-p-4">
        <div className="tw-flex tw-justify-between tw-items-start">
          <div>
            <p className="tw-text-lg tw-font-semibold tw-text-gray-900 tw-mb-0 tw-pb-0">
              Input Schema
            </p>
            <p className="tw-text-gray-500 tw-text-sm tw-mt-0 tw-pt-0">
              The Input Schema controls which fields will be enriched
            </p>
          </div>
          {result_exists && (
            <Button
              variant="textual-blue-alt"
              size="sm"
              compact="very"
              onClick={onViewResult}
            >
              <span className="tw-flex tw-items-center tw-gap-2">
                <Icon name="arrow_back" /> View result
              </span>
            </Button>
          )}
        </div>

        <div className="tw-flex tw-items-start tw-gap-2">
          <div className="tw-flex-1">
            <Dropdown
              testIdBase="enrich-fieldset-dropdown"
              options={fieldsetOptions}
              value={selectedFieldset}
              onChange={(value) => {
                onFieldsetChange(value as number);
              }}
              placeholder="Select an Input Schema"
              disabled={disabled}
            />
          </div>
        </div>

        {isValidating && (
          <div className="tw-mt-4 tw-flex tw-justify-center tw-py-4">
            <Spinner />
          </div>
        )}

        {validationError && (
          <div className="tw-mt-4 tw-p-3 tw-bg-red-50 tw-text-red-700 tw-rounded tw-text-sm">
            {validationError}
          </div>
        )}

        {!isValidating && !validationError && validationResults.length > 0 && (
          <div className="tw-mt-4 tw-border-t tw-border-gray-200 tw-pt-4">
            <div className="tw-flex tw-items-center tw-justify-between tw-mb-3">
              <p className="tw-text-sm tw-font-medium tw-text-gray-700 tw-m-0">
                Fields
              </p>
              <Button
                variant="textual-blue"
                onClick={onEnrich}
                size="sm"
                compact
                disabled={
                  !selectedFieldset ||
                  isEnriching ||
                  disabled ||
                  !allFieldsValid ||
                  isFileInvalid
                }
              >
                {isEnriching ? (
                  "Enriching..."
                ) : (
                  <span className="tw-flex tw-items-center tw-gap-2">
                    <Icon name="auto_fix_high" />
                    Enrich
                  </span>
                )}
              </Button>
            </div>

            <div className="tw-space-y-2">
              {sortedValidationResults.map((field) => {
                const isWarning =
                  field.is_valid_field && !!field.existing_pim_value;
                return (
                  <div
                    key={field.key}
                    className="tw-flex tw-items-center tw-justify-between tw-py-2 tw-px-3 tw-bg-gray-50 tw-rounded hover:tw-bg-gray-100 tw-transition-colors"
                  >
                    <div className="tw-flex tw-items-center tw-gap-3 tw-flex-1 tw-min-w-0">
                      <Icon
                        name={
                          !field.is_valid_field
                            ? "cancel"
                            : isWarning
                            ? "warning"
                            : "check_circle"
                        }
                        className={
                          !field.is_valid_field
                            ? "tw-text-red-600"
                            : isWarning
                            ? "tw-text-yellow-500"
                            : "tw-text-green-600"
                        }
                      />
                      <span className="tw-text-sm tw-font-medium tw-text-gray-900 tw-truncate">
                        {field.key}
                      </span>
                      {!field.is_valid_field && field.error && (
                        <span className="tw-text-sm tw-text-red-600 tw-truncate">
                          {field.error}
                        </span>
                      )}
                      {isWarning && (
                        <span className="tw-text-sm tw-text-yellow-600 tw-truncate">
                          Will not be Enriched, has existing value:{" "}
                          {field.existing_pim_value}
                        </span>
                      )}
                    </div>
                    {field.customer_field_id ? (
                      <Button
                        onClick={() => handleEditField(field)}
                        className="tw-p-1 tw-text-gray-500 !tw-bg-transparent !tw-border-0 hover:!tw-brightness-100 hover:!tw-text-gray-500 !tw-border-gray-500"
                        title="Edit field configuration"
                        borderless
                        size="sm"
                      >
                        <Icon name="settings" className="tw-text-base" />
                      </Button>
                    ) : (
                      <Button
                        onClick={() => handleCreateField(field.key)}
                        disabled={isCreating}
                        className="tw-p-1 tw-text-gray-500 !tw-bg-transparent !tw-border-0 hover:!tw-brightness-100 hover:!tw-text-gray-500 !tw-border-gray-500"
                        title="Create PIM Field"
                        borderless
                        size="sm"
                      >
                        <Icon name="add" className="tw-text-base" />
                      </Button>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {editingFieldId && (
        <CustomerFieldEditModal
          customerFieldId={editingFieldId}
          onClose={() => {
            setEditingFieldId(null);
          }}
          onSave={handleModalSave}
        />
      )}
    </div>
  );
};
